import { commonFilters, IFiltersPagination, IResponse } from "@app/common/types/http";
import {
  HTTP_ADMIN,
  HTTP_ADMISSION,
  HTTP_BASE,
  HTTP_DISPENSARY,
  HTTP_MEDICAL,
  HTTP_PAYROLL,
  HTTP_SECURITY
} from "@app/config/axios";
import {
  BankEntities,
  CompanyClientList,
  CompanyList,
  CompanyTypeList,
  ContractList,
  DepositType,
  DoctorList,
  Extensions,
  Franchise,
  Terminal,
  IAffilitationType,
  IEducationLevel,
  IEthnicGroup,
  ICashClosingConfig,
  ICashClosingConfigFilters,
  ICity,
  IClientContract,
  IContractParams,
  IContractPopulation,
  IContractPopulationParams,
  ICopaymentRate,
  ICopaymentRateParams,
  IPatientDetail,
  IUser,
  IUserByModule,
  ICreatePatientPayload,
  ExternalClientList,
  PatientList,
  SiteList,
  StatusList
} from "./types";
import { IPatientPayload } from "@app/features/drugDelivery/order/patient/types";

export async function companyTypeService(filters: commonFilters, token: string) {
  const res = await HTTP_BASE(token).get<IResponse<CompanyTypeList[]>>(
    "/medical/client/companytype/",
    { params: filters }
  );
  return res.data;
}
export async function listPatientService(
  filters: { orderByType?: string } & IFiltersPagination,
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<PatientList[]>>(
    "/medical/patient/patients-list",
    { params: filters }
  );
  return res.data;
}
export async function listExternalClientsService(
  filters: { orderByType?: string } & IFiltersPagination,
  token: string
) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<ExternalClientList[]>>(
    "/dispensaries/external-clients",
    {
      params: filters
    }
  );
  return res.data;
}
export async function patientInformationService(
  filters: { id?: string | number } & IFiltersPagination,
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<IPatientDetail>>("/medical/patient/", {
    params: filters
  });
  return res.data;
}
export async function updatePatientInformationService(payload: IPatientPayload, token: string) {
  const res = await HTTP_BASE(token).put<IResponse<IPatientDetail>>("/medical/patient/", payload);
  return res.data;
}
export async function createPatientService(payload: ICreatePatientPayload, token: string) {
  const res = await HTTP_DISPENSARY(token).post<IResponse<ExternalClientList>>(
    "/dispensaries/external-clients",
    payload
  );
  return res.data;
}
export async function companyClientService(
  filters: { contractCompanyType?: string } & commonFilters,
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<CompanyClientList[]>>("/medical/client/", {
    params: filters
  });
  return res.data;
}
export async function listSitesService(
  filters: { modality?: string } & commonFilters,
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<SiteList[]>>("/assistance/generals/site/", {
    params: filters
  });
  return res.data;
}
export async function doctorListService(filters: commonFilters, token: string) {
  const res = await HTTP_BASE(token).get<IResponse<DoctorList[]>>("/medical/generals/medical/", {
    params: filters
  });
  return res.data;
}
export async function statusListService(filters: { staFunctionality: string }, token: string) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<StatusList[]>>("/status/", {
    params: filters
  });
  return res.data;
}

export async function documentTypeService(token: string) {
  const res = await HTTP_PAYROLL(token).get<IResponse<StatusList[]>>("/payroll/document_types/");
  return res.data;
}

export async function companyListService(token: string, eaccount: number) {
  const res = await HTTP_MEDICAL(token).get<IResponse<CompanyList[]>>("/generals/company/", {
    params: { eaccount: eaccount }
  });
  return res.data;
}
export async function contractListService(
  filters: { slim: number; corporateClient: number },
  token: string
) {
  const res = await HTTP_MEDICAL(token).get<IResponse<ContractList[]>>("/medicine/getContract/", {
    params: filters
  });
  return res.data;
}
export async function extensionListService(token: string) {
  const res = await HTTP_DISPENSARY(token).get<IResponse<Extensions[]>>("/fileExtensions/");
  return res.data;
}

export async function listSitesToStartService(
  filters: { userId?: number } & commonFilters,
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<{ sites: SiteList[] }>>(
    "/assistance/admissions/sites/",
    {
      params: filters
    }
  );
  return res.data;
}

export async function bankListService(filters: { eaccount: number }, token: string) {
  const res = await HTTP_ADMIN(token).get<IResponse<BankEntities[]>>("/bankingEntities", {
    params: filters
  });
  return res.data;
}
export async function depositTypeService(
  filters: { eaccount: number; active?: number },
  token: string
) {
  const res = await HTTP_ADMISSION(token).get<IResponse<DepositType[]>>(
    "/admissions/depositType/",
    {
      params: filters
    }
  );
  return res.data;
}
export async function franchiseService(filters: { eaccount: number }, token: string) {
  const res = await HTTP_BASE(token).get<IResponse<Franchise[]>>("/accounting/franchise/", {
    params: filters
  });
  return res.data;
}

export async function terminalService(
  filters: { eaccount: number; perpage?: number; page?: number },
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<Terminal[]>>("/accounting/terminal/", {
    params: filters
  });
  return res.data;
}

export async function getCashClosingConfig(filters: ICashClosingConfigFilters, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<ICashClosingConfig>>(
    "/admissions/cashClosingConfig/",
    { params: filters }
  );

  return res.data;
}

export async function getProvincesService(accountId: number, token: string) {
  const res = await HTTP_ADMIN(token).get<IResponse<ICity[]>>("/provinces/", {
    params: { entity_account: accountId }
  });
  return res.data;
}

export async function getCitiesService(token: string, provinceId?: number) {
  const res = await HTTP_ADMIN(token).get<IResponse<ICity[]>>("/cities/", {
    params: { provinceId }
  });
  return res.data;
}

export async function getStratumsService(token: string) {
  const res =
    await HTTP_MEDICAL(token).get<IResponse<{ id: number; name: string }[]>>("/patient/stratums");
  return res.data;
}

export async function getAffiliationTypes(accountId: number, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<IAffilitationType[]>>(
    "/generals/affiliation-types/",
    { params: { eaccount: accountId } }
  );
  return res.data;
}

export async function getEthnicGroups(accountId: number, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<IEthnicGroup[]>>("/generals/ethnic-groups/", {
    params: { eaccount: accountId }
  });
  return res.data;
}

export async function getEducationLevels(accountId: number, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<IEducationLevel[]>>(
    "/generals/education-levels/",
    { params: { eaccount: accountId } }
  );
  return res.data;
}

export async function getContracts(params: Partial<IContractParams>, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<IClientContract[]>>("/client/contract/", {
    params
  });
  return res.data;
}

export async function getContractPopulations(contract: IContractPopulationParams, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<IContractPopulation[]>>(
    "/client/contractpopulation/",
    {
      params: { contract: contract.contract, status: contract.status, eaccount: contract.eaccount }
    }
  );
  return res.data;
}

export async function getCopaymentRates(params: Partial<ICopaymentRateParams>, token: string) {
  const res = await HTTP_MEDICAL(token).get<IResponse<ICopaymentRate[]>>("/client/copaymentRate/", {
    params
  });
  return res.data;
}

export async function getActiveUsersService(
  filters: { page?: number; active?: number },
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<IUser[]>>("/security/user/", {
    params: filters
  });
  return res.data;
}

export async function getUsersByModuleService(
  filters: { id_module: number; eaccount: number },
  token: string
) {
  const res = await HTTP_SECURITY(token).get<IResponse<IUserByModule[]>>("/users/users-by-module", {
    params: filters
  });
  return res.data;
}

export async function getCancellationReasonsService(
  filters: { attributableTo: string },
  token: string
) {
  const res = await HTTP_BASE(token).get<IResponse<import("./types").CancellationReason[]>>(
    "/assistance/generals/cancellationReason/",
    { params: filters }
  );
  return res.data;
}
